import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "./AuthContext";

const VIEW_LOGIN = "login";
const VIEW_FORGOT = "forgot";
const VIEW_FORGOT_SUCCESS = "forgotSuccess";
const VIEW_RESET = "reset";
const VIEW_RESET_SUCCESS = "resetSuccess";

export default function LoginPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [view, setView] = useState(VIEW_LOGIN);
  const [oobCode, setOobCode] = useState(null);
  const [logoWhiteSvg, setLogoWhiteSvg] = useState(null);
  const { signInWithGoogle, signInWithEmailAndPassword, sendPasswordResetEmail, confirmPasswordReset } = useAuth();
  const navigate = useNavigate();

  // Logo (white) inline voor scherpe weergave
  useEffect(() => {
    fetch("/logo-name-white.svg")
      .then((r) => r.text())
      .then((text) => {
        const scaled = text.replace(
          /<svg([^>]*)>/,
          (match) => {
            let attrs = match
              .replace(/\swidth="[^"]*"/, ' width="100%"')
              .replace(/\sheight="[^"]*"/, ' height="100%"');
            if (!/width=/.test(attrs)) attrs = attrs.replace(/<svg/, '<svg width="100%"');
            if (!/height=/.test(attrs)) attrs = attrs.replace(/<svg\s/, '<svg height="100%" ');
            return attrs.replace(/>$/, ' preserveAspectRatio="xMidYMid meet" style="display:block">');
          }
        );
        setLogoWhiteSvg(scaled);
      })
      .catch(() => {});
  }, []);

  // Als gebruiker via e-maillink komt (mode=resetPassword&oobCode=...)
  useEffect(() => {
    const mode = searchParams.get("mode");
    const code = searchParams.get("oobCode");
    if (mode === "resetPassword" && code) {
      setOobCode(code);
      setView(VIEW_RESET);
      setSearchParams({}); // URL opschonen
    }
  }, [searchParams, setSearchParams]);

  const handleSocialLogin = async (provider) => {
    setIsLoading(true);
    setError("");
    
    try {
      let result;
      switch (provider) {
        case 'google':
          result = await signInWithGoogle();
          break;
        default:
          throw new Error('Unknown provider');
      }
      
      console.log('Successfully signed in:', result);
      navigate('/portal');
    } catch (error) {
      console.error('Authentication error:', error);
      // If user doesn't exist with Google, redirect to signup
      if (error.code === 'auth/popup-closed-by-user') {
        setError('Inloggen geannuleerd');
      } else {
        setError(error.message || 'Inloggen mislukt. Probeer opnieuw of maak een account aan.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email) {
      setError("Vul een e-mailadres in");
      return;
    }
    if (!password) {
      setError("Vul je wachtwoord in");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      await signInWithEmailAndPassword(email, password);
      navigate('/portal');
    } catch (err) {
      if (err.code === 'auth/user-not-found' || err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password') {
        setError('Verkeerd e-mailadres of wachtwoord. Heb je nog geen account? Meld je aan.');
      } else {
        setError(err.message || 'Inloggen mislukt');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError("Vul je e-mailadres in");
      return;
    }
    setIsLoading(true);
    setError("");
    try {
      await sendPasswordResetEmail(email.trim());
      setView(VIEW_FORGOT_SUCCESS);
    } catch (err) {
      setError(err.message || "Er ging iets mis. Probeer het later opnieuw.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      setError("Kies een wachtwoord van minimaal 6 tekens");
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setError("De wachtwoorden komen niet overeen");
      return;
    }
    if (!oobCode) {
      setError("De resetlink is ongeldig of verlopen. Vraag een nieuwe aan.");
      return;
    }
    setIsLoading(true);
    setError("");
    try {
      await confirmPasswordReset(oobCode, newPassword);
      setView(VIEW_RESET_SUCCESS);
    } catch (err) {
      if (err.code === "auth/expired-action-code" || err.code === "auth/invalid-action-code") {
        setError("Deze link is verlopen of al gebruikt. Vraag een nieuwe wachtwoordreset aan.");
      } else {
        setError(err.message || "Wachtwoord kon niet worden aangepast.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getProviderIcon = (provider) => {
    switch (provider) {
      case 'google':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg, #3399FF 0%, #2288dd 100%)", position: "relative" }}>
      {/* Header Section */}
      <div style={{
        padding: "32px 20px 100px 20px",
        position: "relative"
      }}>
        {/* Back button and signup link */}
        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "space-between",
          marginBottom: 32
        }}>
          <Link to="/" style={{ 
            color: "#fff", 
            textDecoration: "none",
            fontSize: 18,
            fontWeight: 500
          }}>
            ←
          </Link>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: "#fff", fontSize: 14 }}>Nog geen account?</span>
            <Link to="/signup" style={{
              background: "rgba(255,255,255,0.25)",
              color: "#fff",
              padding: "8px 16px",
              borderRadius: 20,
              textDecoration: "none",
              fontSize: 14,
              fontWeight: 500
            }}>
              Aanmelden
            </Link>
          </div>
        </div>
        
        {/* Brand Name */}
        <div style={{ textAlign: "center", margin: 0 }}>
          {logoWhiteSvg ? (
            <div style={{ height: 48, width: 180, margin: "0 auto", display: "block" }} dangerouslySetInnerHTML={{ __html: logoWhiteSvg }} aria-hidden="true" />
          ) : (
            <img src="/logo-name-white.svg" alt="Slimmer af" style={{ height: 48, width: "auto", display: "block", margin: "0 auto" }} />
          )}
        </div>
      </div>

      {/* White Card - narrow, centered, with space on both sides */}
      <div style={{ padding: "0 clamp(16px, 5vw, 32px)", position: "relative", zIndex: 1 }}>
        <div style={{
          background: "#fff",
          borderRadius: 28,
          marginTop: "-56px",
          marginLeft: "auto",
          marginRight: "auto",
          width: "100%",
          maxWidth: 440,
          padding: "40px 24px",
          position: "relative",
          boxSizing: "border-box"
        }}>
        <div style={{ width: "100%", maxWidth: 420, margin: "0 auto" }}>
        {/* Welcome Section */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <h2 style={{ 
            marginTop: 0, 
            marginBottom: 8, 
            fontSize: 28, 
            fontWeight: 700, 
            color: "#1f2937" 
          }}>
            {view === VIEW_FORGOT || view === VIEW_FORGOT_SUCCESS ? "Wachtwoord vergeten" : view === VIEW_RESET || view === VIEW_RESET_SUCCESS ? "Nieuw wachtwoord" : "Welkom terug"}
          </h2>
          <p style={{ color: "#6b7280", fontSize: 15, margin: 0 }}>
            {view === VIEW_FORGOT && "Vul je e-mailadres in. We sturen je een link om je wachtwoord opnieuw in te stellen."}
            {view === VIEW_FORGOT_SUCCESS && "Controleer je e-mail."}
            {view === VIEW_RESET && "Kies een nieuw wachtwoord (minimaal 6 tekens)."}
            {view === VIEW_RESET_SUCCESS && "Je kunt nu inloggen met je nieuwe wachtwoord."}
            {(view === VIEW_LOGIN || !view) && "Vul je gegevens hieronder in"}
          </p>
        </div>

        {error && (
          <div style={{
            padding: 12,
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: 12,
            color: "#dc2626",
            fontSize: 14,
            marginBottom: 24,
            textAlign: "left"
          }}>
            {error}
          </div>
        )}

        {/* Wachtwoord vergeten: alleen e-mail invullen + stuur link */}
        {(view === VIEW_FORGOT || view === VIEW_FORGOT_SUCCESS) && (
          <>
            {view === VIEW_FORGOT && (
              <form onSubmit={handleForgotSubmit} style={{ marginBottom: 24 }}>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: "block", fontSize: 13, color: "#9ca3af", marginBottom: 8, fontWeight: 500 }}>E-mailadres</label>
                  <input
                    type="email"
                    placeholder="voorbeeld@email.nl"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                    autoComplete="email"
                    style={{ width: "100%", padding: "16px", borderRadius: 12, border: "1px solid #e5e7eb", background: "#fff", color: "#374151", fontSize: 15, boxSizing: "border-box" }}
                  />
                </div>
                <button type="submit" disabled={isLoading} style={{ width: "100%", padding: "16px", borderRadius: 12, background: "linear-gradient(135deg, #3399FF 0%, #2288dd 100%)", color: "#fff", border: "none", fontSize: 16, fontWeight: 600, cursor: isLoading ? "not-allowed" : "pointer", boxShadow: "0 8px 24px rgba(51,153,255,0.35)" }}>
                  {isLoading ? "Bezig…" : "Stuur resetlink"}
                </button>
              </form>
            )}
            {view === VIEW_FORGOT_SUCCESS && (
              <p style={{ color: "#374151", fontSize: 15, marginBottom: 24 }}>
                Als er een account is met dit e-mailadres, heb je een e-mail ontvangen met een link om je wachtwoord opnieuw in te stellen. Controleer ook je spam-map.
              </p>
            )}
            <button type="button" onClick={() => { setView(VIEW_LOGIN); setError(""); }} style={{ background: "none", border: "none", color: "#3399FF", fontSize: 14, cursor: "pointer", textDecoration: "underline", padding: 0 }}>
              ← Terug naar inloggen
            </button>
          </>
        )}

        {/* Nieuw wachtwoord instellen (na klik op link in e-mail) */}
        {(view === VIEW_RESET || view === VIEW_RESET_SUCCESS) && (
          <>
            {view === VIEW_RESET && (
              <form onSubmit={handleResetSubmit} style={{ marginBottom: 24 }}>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: "block", fontSize: 13, color: "#9ca3af", marginBottom: 8, fontWeight: 500 }}>Nieuw wachtwoord * (min. 6 tekens)</label>
                  <input
                    type="password"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={isLoading}
                    autoComplete="new-password"
                    minLength={6}
                    style={{ width: "100%", padding: "16px", borderRadius: 12, border: "1px solid #e5e7eb", background: "#fff", color: "#374151", fontSize: 15, boxSizing: "border-box" }}
                  />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: "block", fontSize: 13, color: "#9ca3af", marginBottom: 8, fontWeight: 500 }}>Wachtwoord bevestigen *</label>
                  <input
                    type="password"
                    placeholder="••••••••"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    disabled={isLoading}
                    autoComplete="new-password"
                    minLength={6}
                    style={{ width: "100%", padding: "16px", borderRadius: 12, border: "1px solid #e5e7eb", background: "#fff", color: "#374151", fontSize: 15, boxSizing: "border-box" }}
                  />
                </div>
                <button type="submit" disabled={isLoading} style={{ width: "100%", padding: "16px", borderRadius: 12, background: "linear-gradient(135deg, #3399FF 0%, #2288dd 100%)", color: "#fff", border: "none", fontSize: 16, fontWeight: 600, cursor: isLoading ? "not-allowed" : "pointer", boxShadow: "0 8px 24px rgba(51,153,255,0.35)" }}>
                  {isLoading ? "Bezig…" : "Wachtwoord opslaan"}
                </button>
              </form>
            )}
            {view === VIEW_RESET_SUCCESS && (
              <button type="button" onClick={() => { setView(VIEW_LOGIN); setError(""); setNewPassword(""); setConfirmNewPassword(""); }} style={{ width: "100%", padding: "16px", borderRadius: 12, background: "linear-gradient(135deg, #3399FF 0%, #2288dd 100%)", color: "#fff", border: "none", fontSize: 16, fontWeight: 600, cursor: "pointer", boxShadow: "0 8px 24px rgba(51,153,255,0.35)" }}>
                Naar inloggen
              </button>
            )}
            {view === VIEW_RESET && (
              <button type="button" onClick={() => { setView(VIEW_LOGIN); setError(""); setOobCode(null); }} style={{ marginTop: 16, background: "none", border: "none", color: "#3399FF", fontSize: 14, cursor: "pointer", textDecoration: "underline", padding: 0 }}>
                ← Terug naar inloggen
              </button>
            )}
          </>
        )}

        {/* Normale inlog (e-mail + wachtwoord + Google) */}
        {view === VIEW_LOGIN && (
        <>
        {/* Gmail/Google login - prominent bovenaan */}
        <div style={{ display: "flex", gap: 12, marginBottom: 24 }}>
          <button
            type="button"
            onClick={() => handleSocialLogin('google')}
            disabled={isLoading}
            style={{
              flex: 1,
              padding: "16px",
              borderRadius: 12,
              border: "1px solid #e5e7eb",
              background: "#fff",
              color: "#374151",
              fontWeight: 500,
              fontSize: 15,
              cursor: isLoading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
            }}
          >
            {getProviderIcon('google')}
            <span>Inloggen met Google</span>
          </button>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24, color: "#9ca3af", fontSize: 14 }}>
          <div style={{ flex: 1, height: 1, background: "#e5e7eb" }}></div>
          <span>Of met e-mail en wachtwoord</span>
          <div style={{ flex: 1, height: 1, background: "#e5e7eb" }}></div>
        </div>

        {/* Login Form */}
        <form onSubmit={handleEmailLogin} style={{ marginBottom: 20 }}>
          {/* Email Field */}
          <div style={{ marginBottom: 20 }}>
            <label style={{
              display: "block",
              fontSize: 13,
              color: "#9ca3af",
              marginBottom: 8,
              fontWeight: 500
            }}>
              E-mailadres
            </label>
            <input
              type="email"
              placeholder="voorbeeld@email.nl"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              autoComplete="email"
              style={{
                width: "100%",
                padding: "16px",
                borderRadius: 12,
                border: "1px solid #e5e7eb",
                background: "#fff",
                color: "#374151",
                fontSize: 15,
                boxSizing: "border-box"
              }}
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{
              display: "block",
              fontSize: 13,
              color: "#9ca3af",
              marginBottom: 8,
              fontWeight: 500
            }}>
              Wachtwoord
            </label>
            <input
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              autoComplete="current-password"
              style={{
                width: "100%",
                padding: "16px",
                borderRadius: 12,
                border: "1px solid #e5e7eb",
                background: "#fff",
                color: "#374151",
                fontSize: 15,
                boxSizing: "border-box"
              }}
            />
          </div>

          {/* Sign In Button */}
          <button
            type="submit"
            disabled={isLoading}
            style={{
              width: "100%",
              padding: "16px",
              borderRadius: 12,
              background: "linear-gradient(135deg, #3399FF 0%, #2288dd 100%)",
              color: "#ffffff",
              border: "none",
              fontSize: 16,
              fontWeight: 600,
              cursor: isLoading ? "not-allowed" : "pointer",
              boxShadow: "0 8px 24px rgba(51,153,255,0.35)",
              marginBottom: 16
            }}
          >
            {isLoading ? 'Bezig met inloggen...' : 'Inloggen'}
          </button>
        </form>

        {/* Forgot Password */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <button
            type="button"
            onClick={() => { setView(VIEW_FORGOT); setError(""); }}
            style={{
              background: "none",
              border: "none",
              color: "#6b7280",
              fontSize: 14,
              cursor: "pointer",
              textDecoration: "none",
              padding: 0
            }}
          >
            Wachtwoord vergeten?
          </button>
        </div>

        {/* Terms */}
        <div style={{ 
          paddingTop: 24,
          textAlign: "center",
          fontSize: 12,
          color: "#9ca3af",
          lineHeight: 1.6
        }}>
          <p style={{ margin: 0 }}>
            Door in te loggen ga je akkoord met onze{" "}
            <a href="#" style={{ color: "#3399FF", textDecoration: "none" }}>voorwaarden</a> en{" "}
            <a href="#" style={{ color: "#3399FF", textDecoration: "none" }}>privacybeleid</a>
          </p>
        </div>
        </>
        )}
        </div>
        </div>
      </div>
      {/* Spacer so there is always blue gradient visible below the card on mobile */}
      <div style={{ minHeight: 80 }} aria-hidden="true" />
    </div>
  );
}
